import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Checkbox, 
  FormControlLabel, 
  Button, 
  Paper,
  Grid,
  Alert,
  Snackbar,
  InputAdornment,
  CircularProgress,
  Divider,
  FormGroup,
  FormLabel,
  Chip,
  Autocomplete,
  Switch,
  IconButton,
  Tooltip,
} from '@mui/material';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import LinkIcon from '@mui/icons-material/Link';
import {
  fetchBranchById,
  updateBranch,
  fetchBranchCities,
  assignBranchCities,
  fetchBranches,
  fetchBranchConnections,
  assignBranchConnections,
} from '../branchesApi';
import { fetchAllFilters, fetchBranchFilters, assignBranchFilters } from '../api/branchFiltersApi';
import { fetchCities } from '../citiesApi';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';

// Fix for default marker icon
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

let DefaultIcon = L.icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

function LocationMarker({ position, setPosition }) {
  const map = useMap();
  
  useEffect(() => {
    if (position) {
      map.flyTo(position, map.getZoom());
    }
  }, [position, map]);

  useMapEvents({
    click(e) {
      setPosition(e.latlng);
    },
  });

  return position === null ? null : (
    <Marker position={position} draggable={true} eventHandlers={{
      dragend: (e) => {
        setPosition(e.target.getLatLng());
      }
    }}>
    </Marker>
  );
}

export default function EditBranch() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [formData, setFormData] = useState({
    id: 0,
    name: '',
    address: '',
    phoneNumber: '',
    facebookUrl: '',
    instagramUrl: '',
    description: '',
    deliveryRangeKm: 0,
    isActive: true,
    openingTime: '08:00',
    closingTime: '23:00',
    deliveryFeeType: 'fixedAmount',
    deliveryFeeValue: 0,
    image: '',
    displayOrder: '',
    isFeatured: false,
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [currentImage, setCurrentImage] = useState(null);
  const [position, setPosition] = useState({ lat: 15.3694, lng: 44.1910 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const [filters, setFilters] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState([]);

  // Cities state
  const [cities, setCities] = useState([]);
  const [selectedCities, setSelectedCities] = useState([]);

  // 🆕 Connections state
  const [allBranches, setAllBranches] = useState([]);
  const [selectedConnections, setSelectedConnections] = useState([]);

  useEffect(() => {
    const fetchBranch = async () => {
      try {
        const branch = await fetchBranchById(id);
        
        let phone = branch.phoneNumber || branch.PhoneNumber || '';
        if (phone.startsWith('+967')) phone = phone.substring(4);
        else if (phone.startsWith('00967')) phone = phone.substring(5);
        else if (phone.startsWith('967')) phone = phone.substring(3);

        setFormData({
          id: branch.id || branch.Id,
          name: branch.name || branch.Name || '',
          address: branch.address || branch.Address || '',
          phoneNumber: phone,
          facebookUrl: branch.facebookUrl || branch.FacebookUrl || '',
          instagramUrl: branch.instagramUrl || branch.InstagramUrl || '',          
          description: branch.description || branch.Description || '',
          deliveryRangeKm: branch.deliveryRangeKm || branch.DeliveryRangeKm || 0,
          isActive: branch.isActive !== undefined ? branch.isActive : branch.IsActive,
          openingTime: branch.openingTime || branch.OpeningTime || '08:00',
          closingTime: branch.closingTime || branch.ClosingTime || '23:00',
          deliveryFeeType: branch.deliveryFeeType || branch.deliveryfeetype || 'fixedAmount',
          deliveryFeeValue: branch.deliveryFeeValue || branch.deliveryfeevalue || 0,
          image: '',
          displayOrder: branch.displayOrder ?? branch.DisplayOrder ?? '',
          isFeatured: branch.isFeatured ?? branch.IsFeatured ?? false,
        });
        setCurrentImage(branch.ImageUrl || branch.imageUrl || branch.image || branch.Image || null);
        const lat = branch.latitude || branch.Latitude;
        const lng = branch.longitude || branch.Longitude;
        
        if (lat && lng) {
          setPosition({ lat: parseFloat(lat), lng: parseFloat(lng) });
        }
      } catch (error) {
        console.error('Error fetching branch:', error);
        setNotification({ open: true, message: t('BranchLoadDetailsFail'), severity: 'error' });
      } finally {
        setLoading(false);
      }
    };

    const fetchFiltersData = async () => {
      const all = await fetchAllFilters();
      setFilters(all);
      const assigned = await fetchBranchFilters(id);
      setSelectedFilters(assigned.map(f => f.Id));
    };

    const fetchCitiesData = async () => {
      try {
        const allCities = await fetchCities();
        setCities(allCities);
        const assignedCities = await fetchBranchCities(id);
        setSelectedCities(assignedCities);
      } catch (err) {
        console.error('Error loading cities:', err);
      }
    };

    // 🆕 Load connections
    const fetchConnectionsData = async () => {
      try {
        const branches = await fetchBranches();
        const otherBranches = (branches || []).filter(b => String(b.Id || b.id) !== String(id));
        setAllBranches(otherBranches);
        const connections = await fetchBranchConnections(id);
        setSelectedConnections(connections || []);
      } catch (err) {
        console.error('Error loading connections:', err);
      }
    };

    if (id) {
      fetchBranch();
      fetchFiltersData();
      fetchCitiesData();
      fetchConnectionsData();
    }
  }, [id]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData((prev) => ({ ...prev, image: reader.result }));
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    if (name === 'phoneNumber') {
      const numericValue = value.replace(/[^0-9]/g, '').slice(0, 9);
      setFormData({ ...formData, [name]: numericValue });
    } else if (name === 'deliveryFeeValue') {
      setFormData({ ...formData, [name]: Math.max(0, Number(value)) });
    } else if (name === 'displayOrder') {
      setFormData({ ...formData, [name]: value.replace(/[^0-9]/g, '') });
    } else {
      setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
    }
  };

  const handleGetCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setPosition({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        },
        (err) => {
          console.error(err);
          setNotification({ open: true, message: t('Error_Getting_Location'), severity: 'error' });
        }
      );
    } else {
      setNotification({ open: true, message: t('Geolocation_Not_Supported'), severity: 'error' });
    }
  };

  const handleFilterChange = (fid) => {
    setSelectedFilters((prev) =>
      prev.includes(fid) ? prev.filter(id => id !== fid) : [...prev, fid]
    );
  };

  // 🆕 Display order helpers
  const handleDisplayOrderUp = () => {
    setFormData(prev => ({
      ...prev,
      displayOrder: String(Math.max(0, Number(prev.displayOrder || 0) - 1))
    }));
  };

  const handleDisplayOrderDown = () => {
    setFormData(prev => ({
      ...prev,
      displayOrder: String(Number(prev.displayOrder || 0) + 1)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.phoneNumber && formData.phoneNumber.length !== 9) {
      setNotification({ open: true, message: t('PhoneValidation'), severity: 'error' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...formData,
        phoneNumber: `+967${formData.phoneNumber}`,
        latitude: position.lat,
        longitude: position.lng,
        filterIds: selectedFilters,
        cityIds: selectedCities.map(city => city.Id || city.id),
        displayOrder: formData.displayOrder !== '' ? Number(formData.displayOrder) : null,
        isFeatured: formData.isFeatured,
      };
      await updateBranch(id, payload);
      await assignBranchFilters(id, selectedFilters);
      await assignBranchCities(id, selectedCities.map(city => city.Id || city.id));
      // 🆕 Save connections
      await assignBranchConnections(id, selectedConnections.map(b => b.Id || b.id));
      setNotification({ open: true, message: t('BranchUpdateSuccess'), severity: 'success' });
      setTimeout(() => navigate('/admin/branches'), 1500);
    } catch (error) {
      console.error('Error updating branch:', error);
      setNotification({ open: true, message: t('BranchUpdateFail'), severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          {t('EditBranch')}
        </Typography>
        <Button 
          variant="outlined" 
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/admin/branches')}
        >
          {t('BackToList')}
        </Button>
      </Box>

      <Paper sx={{ p: 4 }}>
        <Typography variant="h6" gutterBottom color="primary">
          {t('BranchDetails')}
        </Typography>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 5 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  fullWidth
                  label={t('Name')}
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
                <TextField
                  fullWidth
                  label={t('Address')}
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  multiline
                  rows={3}
                />

                <TextField
                  fullWidth
                  label={t('Phone_Number')}
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Box sx={{ 
                          bgcolor: 'primary.main', 
                          color: 'white', 
                          px: 1, 
                          py: 0.5, 
                          borderRadius: 1,
                          mr: 1,
                          fontWeight: 'bold'
                        }}>
                          +967
                        </Box>
                      </InputAdornment>
                    ),
                  }}
                  helperText={`${formData.phoneNumber.length}/9 ${t('Digits')}`}
                  error={formData.phoneNumber.length > 0 && formData.phoneNumber.length !== 9}
                />

                <TextField
                  fullWidth
                  label={t('Description')}
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  multiline
                  rows={2}
                  variant="outlined"
                  placeholder="Branch description..."
                />

                {/* Image Upload Field */}
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {t('BranchImage')}
                  </Typography>
                  <Button variant="contained" component="label">
                    {t('UploadImage')}
                    <input type="file" accept="image/*" hidden onChange={handleImageChange} />
                  </Button>
                  {imagePreview && (
                    <Box mt={2}>
                      <img src={imagePreview} alt="Preview" style={{ maxWidth: 180, maxHeight: 120, borderRadius: 8, border: '1px solid #ccc' }} />
                    </Box>
                  )}
                  {!imagePreview && currentImage && (
                    <Box mt={2}>
                      <img src={currentImage} alt="Current" style={{ maxWidth: 180, maxHeight: 120, borderRadius: 8, border: '1px solid #ccc' }} />
                    </Box>
                  )}
                </Box>

                <Grid container spacing={2}>
                  <Grid size={{ xs: 6 }}>
                    <TextField
                      fullWidth
                      label={t('OpeningTime')}
                      name="openingTime"
                      type="time"
                      value={formData.openingTime}
                      onChange={handleChange}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ step: 300 }}
                    />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <TextField
                      fullWidth
                      label={t('ClosingTime')}
                      name="closingTime"
                      type="time"
                      value={formData.closingTime}
                      onChange={handleChange}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ step: 300 }}
                    />
                  </Grid>
                </Grid>

                <TextField
                  fullWidth
                  label={t('DeliveryRangeKm')}
                  name="deliveryRangeKm"
                  type="number"
                  value={formData.deliveryRangeKm}
                  onChange={handleChange}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">{t('Km')}</InputAdornment>,
                  }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.isActive}
                      onChange={handleChange}
                      name="isActive"
                      color="primary"
                    />
                  }
                  label={t('Active')}
                />
                
                <Box>
                  <Typography variant="subtitle2" gutterBottom>{t('Coordinates')}</Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 6 }}>
                      <TextField
                        fullWidth
                        label={t('Latitude')}
                        value={position.lat}
                        size="small"
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <TextField
                        fullWidth
                        label={t('Longitude')}
                        value={position.lng}
                        size="small"
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>
                  </Grid>
                </Box>

                {/* Social Media Section */}
                <Grid size={{ xs: 12 }}>
                  <Typography variant="h6" sx={{ mt: 2, mb: 2 }}>{t('socialMedia')}</Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <TextField
                        fullWidth
                        label="Facebook URL"
                        name="facebookUrl"
                        value={formData.facebookUrl}
                        onChange={handleChange}
                        InputProps={{
                          startAdornment: <InputAdornment position="start"><FacebookIcon color="primary" /></InputAdornment>,
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <TextField
                        fullWidth
                        label="Instagram URL"
                        name="instagramUrl"
                        value={formData.instagramUrl}
                        onChange={handleChange}
                        InputProps={{
                          startAdornment: <InputAdornment position="start"><InstagramIcon color="secondary" /></InputAdornment>,
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                {/* Delivery Fee Section */}
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" color="primary" fontWeight="bold">
                  {t('DeliveryFee')}
                </Typography>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={7}>
                    <TextField
                      select
                      fullWidth
                      label={t('DeliveryFeeType')}
                      name="deliveryFeeType"
                      value={formData.deliveryFeeType}
                      onChange={handleChange}
                      SelectProps={{ native: true }}
                      variant="outlined"
                    >
                      <option value="fixedAmount">{t('FixedAmount')}</option>
                      <option value="byKm">{t('ByKm')}</option>
                    </TextField>
                  </Grid>
                  <Grid item xs={5}>
                    <TextField
                      fullWidth
                      label={formData.deliveryFeeType === 'byKm' ? t('FeePerKm') : t('FixedFeeAmount')}
                      name="deliveryFeeValue"
                      type="number"
                      value={formData.deliveryFeeValue}
                      onChange={handleChange}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">{formData.deliveryFeeType === 'byKm' ? t('PerKm') : t('Currency')}</InputAdornment>
                      }}
                      variant="outlined"
                      inputProps={{ min: 0, step: 0.01 }}
                    />
                  </Grid>
                </Grid>

                {/* ═══════════════════════════════════════════════════ */}
                {/* 🆕 DISPLAY ORDER & FEATURED SECTION                */}
                {/* ═══════════════════════════════════════════════════ */}
                <Divider sx={{ my: 3 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <SwapVertIcon sx={{ color: '#7B1FA2' }} />
                  <Typography variant="subtitle1" sx={{ color: '#7B1FA2' }} fontWeight="bold">
                    {t('DisplaySettings')}
                  </Typography>
                </Box>

                {/* Display Order */}
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {t('DisplayOrderDesc')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Tooltip title={t('MoveUp')}>
                    <IconButton
                      onClick={handleDisplayOrderUp}
                      sx={{
                        bgcolor: '#E8F5E9',
                        '&:hover': { bgcolor: '#C8E6C9' },
                        width: 44,
                        height: 44,
                      }}
                    >
                      <ArrowUpwardIcon sx={{ color: '#4CAF50' }} />
                    </IconButton>
                  </Tooltip>
                  <TextField
                    name="displayOrder"
                    value={formData.displayOrder}
                    onChange={handleChange}
                    type="text"
                    inputProps={{ 
                      style: { textAlign: 'center', fontSize: 20, fontWeight: 700, color: '#7B1FA2' },
                      inputMode: 'numeric',
                    }}
                    sx={{
                      width: 100,
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: '#7B1FA2', borderWidth: 2 },
                        '&:hover fieldset': { borderColor: '#7B1FA2' },
                        '&.Mui-focused fieldset': { borderColor: '#7B1FA2' },
                      },
                    }}
                    placeholder="0"
                  />
                  <Tooltip title={t('MoveDown')}>
                    <IconButton
                      onClick={handleDisplayOrderDown}
                      sx={{
                        bgcolor: '#FFF3E0',
                        '&:hover': { bgcolor: '#FFE0B2' },
                        width: 44,
                        height: 44,
                      }}
                    >
                      <ArrowDownwardIcon sx={{ color: '#FF9800' }} />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                  {t('DisplayOrderHint')}
                </Typography>

                {/* Featured Toggle */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    bgcolor: formData.isFeatured ? '#FFF8E1' : '#FAFAFA',
                    border: '1px solid',
                    borderColor: formData.isFeatured ? '#FFD54F' : '#E0E0E0',
                    borderRadius: 2,
                    p: 2,
                    mb: 2,
                    transition: 'all 0.3s',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    {formData.isFeatured ? (
                      <StarIcon sx={{ color: '#FF9800', fontSize: 28 }} />
                    ) : (
                      <StarBorderIcon sx={{ color: '#BDBDBD', fontSize: 28 }} />
                    )}
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {t('FeaturedBranch')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('FeaturedBranchDesc')}
                      </Typography>
                    </Box>
                  </Box>
                  <Switch
                    checked={formData.isFeatured}
                    onChange={(e) => setFormData(prev => ({ ...prev, isFeatured: e.target.checked }))}
                    color="warning"
                  />
                </Box>

                {/* ═══════════════════════════════════════════════════ */}
                {/* Delivery Cities Section                            */}
                {/* ═══════════════════════════════════════════════════ */}
                <Divider sx={{ my: 3 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <LocalShippingIcon color="primary" />
                  <Typography variant="subtitle1" color="primary" fontWeight="bold">
                    {t('DeliveryCities')}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('DeliveryCitiesDesc')}
                </Typography>

                {/* Select All Checkbox */}
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={cities.length > 0 && selectedCities.length === cities.length}
                      indeterminate={selectedCities.length > 0 && selectedCities.length < cities.length}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedCities([...cities]);
                        } else {
                          setSelectedCities([]);
                        }
                      }}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2" fontWeight="bold">
                      {t('SelectAllCities')} ({cities.length})
                    </Typography>
                  }
                  sx={{ mb: 1 }}
                />

                <Autocomplete
                  multiple
                  options={cities}
                  getOptionLabel={(option) => option.Name || option.name || ''}
                  value={selectedCities}
                  onChange={(event, newValue) => setSelectedCities(newValue)}
                  isOptionEqualToValue={(option, value) => (option.Id || option.id) === (value.Id || value.id)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label={t('SelectCities')}
                      placeholder={t('SearchCities')}
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option.Id || option.id}
                        label={option.Name || option.name}
                        color="primary"
                        variant="outlined"
                        size="small"
                      />
                    ))
                  }
                  noOptionsText={t('NoCitiesFound')}
                  sx={{ mb: 1 }}
                />
                {selectedCities.length > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    {selectedCities.length} {t('CitiesSelected')}
                  </Typography>
                )}
                {selectedCities.length === 0 && (
                  <Typography variant="caption" color="warning.main">
                    {t('NoCitiesSelectedWarning')}
                  </Typography>
                )}

                {/* ═══════════════════════════════════════════════════ */}
                {/* 🆕 CONNECTED BRANCHES SECTION                      */}
                {/* ═══════════════════════════════════════════════════ */}
                <Divider sx={{ my: 3 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <LinkIcon sx={{ color: '#5C6BC0' }} />
                  <Typography variant="subtitle1" sx={{ color: '#5C6BC0' }} fontWeight="bold">
                    {t('ConnectedBranches')}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('ConnectedBranchesDesc')}
                </Typography>

                <Autocomplete
                  multiple
                  options={allBranches}
                  getOptionLabel={(option) => {
                    const name = option.Name || option.name || '';
                    const addr = option.Address || option.address || '';
                    return addr ? `${name} — ${addr}` : name;
                  }}
                  value={selectedConnections}
                  onChange={(event, newValue) => setSelectedConnections(newValue)}
                  isOptionEqualToValue={(option, value) => (option.Id || option.id) === (value.Id || value.id)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label={t('SelectBranches')}
                      placeholder={t('SearchBranches')}
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option.Id || option.id}
                        label={option.Name || option.name}
                        icon={<LinkIcon sx={{ fontSize: 16 }} />}
                        color="primary"
                        variant="outlined"
                        size="small"
                        sx={{ borderColor: '#5C6BC0', color: '#5C6BC0' }}
                      />
                    ))
                  }
                  noOptionsText={t('NoBranchesFound')}
                  sx={{ mb: 1 }}
                />
                {selectedConnections.length > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    {selectedConnections.length} {t('BranchesConnected')}
                  </Typography>
                )}
                {selectedConnections.length === 0 && (
                  <Typography variant="caption" color="text.secondary">
                    {t('NoConnectionsYet')}
                  </Typography>
                )}
              </Box>
            </Grid>

            <Grid size={{ xs: 12, md: 7 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold">{t('PinLocationOnMap')}</Typography>
                <Button 
                  variant="contained" 
                  color="info" 
                  size="small" 
                  startIcon={<MyLocationIcon />}
                  onClick={handleGetCurrentLocation}
                >
                  {t('UseMyLocation')}
                </Button>
              </Box>
              <Box sx={{ height: 500, width: '100%', borderRadius: 1, overflow: 'hidden', border: '1px solid #ccc' }}>
                <MapContainer center={position} zoom={13} style={{ height: '100%', width: '100%' }} attributionControl={false}>
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <LocationMarker position={position} setPosition={setPosition} />
                </MapContainer>
              </Box>
              <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                {t('DragMarkerInstruction')}
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              {/* Filters Selection */}
              <Divider sx={{ my: 2 }} />
              <FormLabel component="legend">{t('Filters')}</FormLabel>
              <FormGroup row>
                {filters.map((filter) => (
                  <FormControlLabel
                    key={filter.Id}
                    control={
                      <Checkbox
                        checked={selectedFilters.includes(filter.Id)}
                        onChange={() => handleFilterChange(filter.Id)}
                      />
                    }
                    label={filter.name}
                  />
                ))}
              </FormGroup>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Button 
                type="submit" 
                variant="contained" 
                color="primary" 
                size="large"
                disabled={saving}
              >
                {saving ? t('Updating') : t('UpdateBranch')}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>

      <Snackbar 
        open={notification.open} 
        autoHideDuration={6000} 
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseNotification} severity={notification.severity} sx={{ width: '100%' }}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}